import React, { useState, useEffect, useMemo, useCallback, useLayoutEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Plus, Search, Image, Eye, Trash2, FileText, CheckCircle, XCircle, Layers, ClipboardCheck, UploadCloud, AlertTriangle, List, RotateCcw, ArrowRight, X } from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '../../components/admin/Sidebar';
import Header from '../../components/admin/Header';
import ConfirmModal from '../../components/ConfirmModal';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { formatDate, STAGES, getArtworkPreviewKind, getCloudinaryThumbnailUrl, getArtworkSupplierDisplayName, getStage3SupplierSubmissionStatus, getStageSupplierSubmissionStatusForUser, getPendingReviewersForArtwork, getPendingReviewersForUser, doesArtworkRequireAdminFinalize, hasPendingReviewForRole } from '../../utils/helpers';
import {
  DEFAULT_ARTWORK_LIST_TAB,
  getStoredArtworkListTab,
  setStoredArtworkListTab,
} from './artworkListTabStorage';

const reviewerNeedsStage = (artwork, stage, role, reviewerId, reviewerEmail, reviewerName) =>
  hasPendingReviewForRole(artwork[`stage${stage}`], role, artwork, stage, reviewerId, reviewerEmail, reviewerName);

const isRoleReadyForReopen = (approvals = [], role) => {
  const roleApprovals = approvals.filter(approval => approval.role === role);
  if (!roleApprovals.length) return false;
  const hasHead = ['rd', 'regulatory', 'qa'].includes(role) && roleApprovals.some(approval => approval.teamLevel === 'head');
  return hasHead
    ? roleApprovals.some(approval => approval.teamLevel === 'head' && approval.status === 'approved')
    : roleApprovals.every(approval => approval.status === 'approved');
};

const isStage4ReviewComplete = (submission) => (
  isRoleReadyForReopen(submission.approvals || [], 'rd') &&
  isRoleReadyForReopen(submission.approvals || [], 'regulatory') &&
  isRoleReadyForReopen(submission.approvals || [], 'qa') &&
  isRoleReadyForReopen(submission.approvals || [], 'cco')
);

const supplierKey = (supplier) => String(supplier?._id || supplier || '');

const submissionForSupplier = (artwork, stageNum, supplierId) => (
  (artwork[`stage${stageNum}`]?.submissions || []).find(
    submission => supplierKey(submission?.supplierId) === supplierId
  ) || null
);

const hasSupplierFile = (submission) => Boolean(
  submission?.artworkUrl || submission?.artworkFileName || submission?.artworkData
);

const isArtworkFullyCompleted = (artwork) => {
  const assignedSuppliers = artwork.supplierAssignmentInfo?.assignedSuppliers?.length
    ? artwork.supplierAssignmentInfo.assignedSuppliers
    : artwork.suppliers || [];
  const supplierIds = assignedSuppliers.map(supplierKey).filter(Boolean);
  return supplierIds.length > 0 && supplierIds.every((supplierId) => {
    const stage3 = submissionForSupplier(artwork, 3, supplierId);
    const stage4 = submissionForSupplier(artwork, 4, supplierId);
    return stage3?.status === 'completed' &&
      stage4?.status === 'completed' &&
      Boolean(stage4.adminApproval?.uploadedAt);
  });
};

const getArtworkSupplierIds = (artwork) => {
  const assigned = artwork.supplierAssignmentInfo?.assignedSuppliers?.length
    ? artwork.supplierAssignmentInfo.assignedSuppliers
    : artwork.suppliers || [];
  return assigned.map(supplierKey).filter(Boolean);
};

const supplierMatchesWorkflowTab = (artwork, supplierId, tab) => {
  const stage3 = submissionForSupplier(artwork, 3, supplierId);
  const stage4 = submissionForSupplier(artwork, 4, supplierId);
  const stage3Complete = stage3?.status === 'completed';
  const stage4ReadyForFinalCopy = isStage4ReviewComplete(stage4 || {}) && !stage4?.adminApproval?.uploadedAt;
  const changesRequested = [stage3, stage4].some(submission =>
    (submission?.approvals || []).some(approval => approval.status === 'changes_requested')
  );

  if (tab === 'stage3') return !stage3Complete && !changesRequested;
  if (tab === 'stage4') return stage3Complete && Boolean(stage4) && !stage4ReadyForFinalCopy && stage4?.status !== 'completed' && !changesRequested;
  if (tab === 'finalize') return stage4ReadyForFinalCopy;
  if (tab === 'changes_requested') return changesRequested;
  return true;
};

const artworkHasSupplierForWorkflowTab = (artwork, tab) =>
  getArtworkSupplierIds(artwork).some(supplierId => supplierMatchesWorkflowTab(artwork, supplierId, tab));

const supplierStageLabel = (submission, stageNum) => {
  if (!submission || !hasSupplierFile(submission)) return stageNum === 3 ? 'Awaiting upload' : 'Not started';
  if ((submission.approvals || []).some(approval => approval.status === 'changes_requested')) return 'Changes requested';
  if (submission.status === 'completed') return 'Completed';
  return 'In review';
};

const badgeStyleForWorkflowState = (label) => {
  if (label === 'Completed' || label === 'Uploaded') return { color: '#166534', background: '#dcfce7' };
  if (label === 'Changes requested') return { color: '#9a3412', background: '#ffedd5' };
  if (label === 'Needs final copy') return { color: '#6d28d9', background: '#ede9fe' };
  return { color: '#475569', background: '#f1f5f9' };
};

const WorkflowBadge = ({ label }) => (
  <span style={{ display: 'inline-flex', padding: '3px 8px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap', ...badgeStyleForWorkflowState(label) }}>
    {label}
  </span>
);

const getReopenChoices = (artwork) => {
  const suppliers = new Map();
  const assignedSuppliers = artwork.supplierAssignmentInfo?.assignedSuppliers || artwork.suppliers || [];
  const assignedNames = new Map(assignedSuppliers.map(supplier => [
    String(supplier?._id || supplier),
    supplier?.name || supplier?.email || ''
  ]));

  const addChoice = (stageNum, submission) => {
    const supplierId = String(submission?.supplierId?._id || submission?.supplierId || '');
    if (!supplierId) return;
    const supplierName = submission.supplierId?.name || submission.supplierId?.email || assignedNames.get(supplierId) || 'Supplier';
    const supplier = suppliers.get(supplierId) || { supplierId, supplierName, stages: [] };
    if (!supplier.stages.includes(stageNum)) supplier.stages.push(stageNum);
    suppliers.set(supplierId, supplier);
  };

  (artwork.stage3?.submissions || []).forEach(submission => {
    if (submission.status === 'completed') addChoice(3, submission);
  });
  (artwork.stage4?.submissions || []).forEach(submission => {
    const awaitingFinalCopy = !submission.adminApproval?.uploadedAt && isStage4ReviewComplete(submission);
    if (submission.status === 'completed' || awaitingFinalCopy) addChoice(4, submission);
  });

  return [...suppliers.values()];
};

export default function ArtworkList() {
  const { isAdmin, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [artworks, setArtworks] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [activeTab, setActiveTab] = useState(() => {
    const fromRouteState = location.state?.activeTab;
    if (fromRouteState) return fromRouteState;
    return getStoredArtworkListTab(DEFAULT_ARTWORK_LIST_TAB);
  });
  const [currentPage, setCurrentPage] = useState(() => Math.max(1, Number(location.state?.currentPage) || 1));
  const filterKey = `${search}|${stageFilter}|${statusFilter}|${brandFilter}|${activeTab}`;
  const previousFilterKey = React.useRef(filterKey);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, id: null, title: '' });
  const [requestChangesArtwork, setRequestChangesArtwork] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const rowsPerPage = 20;
  const reviewerId = user?._id || user?.id;
  const reviewerEmail = user?.email;
  const reviewerName = user?.name;
  const reviewerArtworks = useMemo(() => (
    isAdmin || !user?.role
      ? artworks
      : artworks.filter(artwork => [1, 2, 3, 4].some(stage => reviewerNeedsStage(artwork, stage, user.role, reviewerId, reviewerEmail, reviewerName)))
  ), [artworks, isAdmin, user?.role, reviewerId, reviewerEmail, reviewerName]);
  const isArtworkInStage4 = useCallback(artwork => (
    artwork.overallStatus !== 'completed' &&
    (isAdmin
      ? artwork.currentStage === 4 && !doesArtworkRequireAdminFinalize(artwork)
      : reviewerNeedsStage(artwork, 4, user?.role, reviewerId, reviewerEmail, reviewerName))
  ), [isAdmin, user?.role, reviewerId, reviewerEmail, reviewerName]);

  useEffect(() => {
    (async () => {
      try {
        const query = new URLSearchParams();
        query.set('summary', 'true');
        if (brandFilter) query.set('brand', brandFilter);
        const res = await api.get(`/artworks?${query.toString()}`);
        setArtworks(res.data);
        setFiltered(res.data);
      } catch {} finally { setLoading(false); }
    })();
  }, [brandFilter]);

  useEffect(() => {
    setStoredArtworkListTab(activeTab, DEFAULT_ARTWORK_LIST_TAB);
  }, [activeTab]);

  useEffect(() => {
    if (user && !isAdmin && ['completed', 'changes_requested'].includes(activeTab)) {
      setActiveTab('all');
    }
  }, [user, isAdmin, activeTab]);

  useEffect(() => {
    const handleRefresh = async () => {
      setLoading(true);
      try {
        const query = new URLSearchParams();
        query.set('summary', 'true');
        if (brandFilter) query.set('brand', brandFilter);
        const res = await api.get(`/artworks?${query.toString()}`);
        setArtworks(res.data);
        setFiltered(res.data);
        toast.success('Data refreshed');
      } catch {
        toast.error('Failed to refresh data');
      } finally {
        setLoading(false);
      }
    };

    window.addEventListener('navigationRefresh', handleRefresh);
    return () => window.removeEventListener('navigationRefresh', handleRefresh);
  }, [brandFilter]);

  useLayoutEffect(() => {
    let result = reviewerArtworks;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(a =>
        a.title.toLowerCase().includes(q) ||
        a.sku.toLowerCase().includes(q) ||
        a.designId.toLowerCase().includes(q) ||
        (a.suppliers && a.suppliers.some(s => s.name?.toLowerCase().includes(q)))
      );
    }
    if (stageFilter) result = result.filter(a => a.currentStage === parseInt(stageFilter));
    if (statusFilter) result = result.filter(a => a.overallStatus === statusFilter);
    if (brandFilter) result = result.filter(a => (a.brand || '').toLowerCase() === brandFilter.toLowerCase());

    switch (activeTab) {
      case 'stage1':
        result = result.filter(a => isAdmin ? a.currentStage === 1 : reviewerNeedsStage(a, 1, user?.role, reviewerId, reviewerEmail, reviewerName));
        break;
      case 'stage2':
        result = result.filter(a => isAdmin ? a.currentStage === 2 : reviewerNeedsStage(a, 2, user?.role, reviewerId, reviewerEmail, reviewerName));
        break;
      case 'stage3':
        result = result.filter(a => isAdmin ? artworkHasSupplierForWorkflowTab(a, 'stage3') : reviewerNeedsStage(a, 3, user?.role, reviewerId, reviewerEmail, reviewerName));
        break;
      case 'stage4':
        result = result.filter(a => isAdmin ? artworkHasSupplierForWorkflowTab(a, 'stage4') : isArtworkInStage4(a));
        break;
      case 'finalize':
        result = result.filter(a => artworkHasSupplierForWorkflowTab(a, 'finalize'));
        break;
      case 'completed':
        result = result.filter(isArtworkFullyCompleted);
        break;
      case 'changes_requested':
        result = result.filter(a => artworkHasSupplierForWorkflowTab(a, 'changes_requested') || ['stage1', 'stage2'].some(s => a[s]?.status === 'changes_requested'));
        break;
      default:
        break;
    }

    setFiltered(result);
    if (previousFilterKey.current !== filterKey) {
      setCurrentPage(1);
      previousFilterKey.current = filterKey;
    }
  }, [search, stageFilter, statusFilter, brandFilter, activeTab, reviewerArtworks, filterKey, isAdmin, user?.role, reviewerId, reviewerEmail, reviewerName, isArtworkInStage4]);

  useEffect(() => {
    const pageCount = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
    if (currentPage > pageCount) {
      setCurrentPage(pageCount);
    }
  }, [filtered.length, currentPage, rowsPerPage]);

  const openDeleteModal = (id, title) => {
    setDeleteModal({ isOpen: true, id, title });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, id: null, title: '' });
  };

  const confirmDelete = async () => {
    if (!deleteModal.id) return;

    setDeleting(true);
    try {
      await api.delete(`/artworks/${deleteModal.id}`);
      setArtworks(prev => prev.filter(a => a._id !== deleteModal.id));
      toast.success(`"${deleteModal.title}" deleted successfully`);
      closeDeleteModal();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete artwork');
    } finally {
      setDeleting(false);
    }
  };

  const kpiCards = [
    { key: 'all', label: isAdmin ? 'All artworks' : 'All requiring attention', count: reviewerArtworks.length, icon: List, color: '#475569', background: '#f1f5f9' },
    ...[1, 2, 3, 4].map(stage => ({
      key: `stage${stage}`,
      label: `Stage ${stage}`,
        count: reviewerArtworks.filter(artwork => isAdmin && stage >= 3
          ? artworkHasSupplierForWorkflowTab(artwork, `stage${stage}`)
          : stage === 4
            ? isArtworkInStage4(artwork)
            : isAdmin
              ? artwork.currentStage === stage
            : reviewerNeedsStage(artwork, stage, user?.role, reviewerId, reviewerEmail, reviewerName)
      ).length,
      icon: Layers,
      color: ['#be123c', '#b45309', '#047857', '#0369a1'][stage - 1],
      background: ['#fff1f2', '#fffbeb', '#ecfdf5', '#f0f9ff'][stage - 1]
    })),
    ...(isAdmin ? [{
      key: 'finalize',
      label: 'Needs final copy',
      count: reviewerArtworks.filter(artwork => artworkHasSupplierForWorkflowTab(artwork, 'finalize')).length,
      icon: UploadCloud,
      color: '#7c3aed',
      background: '#f5f3ff'
    }] : []),
    ...(isAdmin ? [
      { key: 'completed', label: 'Completed', count: reviewerArtworks.filter(isArtworkFullyCompleted).length, icon: ClipboardCheck, color: '#15803d', background: '#f0fdf4' },
      { key: 'changes_requested', label: 'Changes requested', count: reviewerArtworks.filter(artwork => ['stage1', 'stage2', 'stage3', 'stage4'].some(stage => artwork[stage]?.status === 'changes_requested')).length, icon: AlertTriangle, color: '#c2410c', background: '#fff7ed' }
    ] : [])
  ];
  const supplierWorkflowTabs = ['stage3', 'stage4', 'finalize', 'completed', 'changes_requested'];
  const showSupplierWorkflow = isAdmin && supplierWorkflowTabs.includes(activeTab);

  return (
    <div className="page-layout" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      <style>{`
        .awl-row { transition: background 0.15s; }
        .awl-row:hover { background: var(--gray-50); }
        .badge-icon { display: inline-flex; align-items: center; gap: 4px; }
        .artwork-kpi-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 12px; margin-bottom: 20px; }
        .artwork-kpi { position: relative; display: flex; align-items: center; gap: 12px; min-height: 78px; padding: 14px 16px; border: 1px solid var(--gray-200); border-radius: 12px; background: var(--white); color: var(--gray-700); text-align: left; cursor: pointer; box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04); transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease; }
        .artwork-kpi:hover { border-color: var(--gray-300); box-shadow: 0 5px 14px rgba(15, 23, 42, 0.08); transform: translateY(-1px); }
        .artwork-kpi.active { border-color: var(--brand-red); box-shadow: 0 0 0 2px rgba(200, 16, 46, 0.12), 0 5px 14px rgba(15, 23, 42, 0.08); }
        .artwork-kpi-icon { display: grid; place-items: center; width: 36px; height: 36px; border-radius: 10px; flex: 0 0 36px; }
        .artwork-kpi-label { display: block; overflow: hidden; color: var(--gray-500); font-size: 0.72rem; font-weight: 700; letter-spacing: 0.02em; line-height: 1.2; text-overflow: ellipsis; white-space: nowrap; }
        .artwork-kpi-value { display: block; margin-top: 3px; color: var(--gray-900); font-size: 1.35rem; font-weight: 800; line-height: 1; }
        .supplier-workflow-table th { text-transform: none; letter-spacing: normal; }
        .supplier-workflow-table .supplier-workflow-group-even td { background: #f8fafc; }
        .supplier-workflow-table .supplier-workflow-group-odd td { background: #ffffff; }
        .supplier-workflow-table .supplier-workflow-group-start td { border-top: 2px solid #dbe3ee; }
        .supplier-workflow-table .supplier-workflow-group-even:hover td, .supplier-workflow-table .supplier-workflow-group-odd:hover td { background: #eff6ff; }
        @media (max-width: 640px) { .artwork-kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } .artwork-kpi { padding: 12px; } }
        .pagination-button { border: 1px solid var(--gray-200); background: var(--white); color: var(--gray-700); padding: 8px 12px; border-radius: 8px; font-size: 0.85rem; cursor: pointer; transition: all 0.2s ease; }
        .pagination-button:hover:not(:disabled) { border-color: var(--brand-red); color: var(--brand-red); }
        .pagination-button.active { background: var(--brand-red); border-color: var(--brand-red); color: white; }
        .pagination-button:disabled { opacity: 0.45; cursor: not-allowed; }
        .reopen-picker-modal { max-width: 640px; overflow: hidden; border: 0; border-radius: 16px; box-shadow: 0 24px 70px rgba(15, 23, 42, 0.24); }
        .reopen-picker-modal .modal-header { align-items: flex-start; padding: 22px 24px; border: 0; background: linear-gradient(135deg, #fff1f2, #fff8f6); }
        .reopen-picker-heading { display: flex; align-items: center; gap: 12px; min-width: 0; }
        .reopen-picker-icon { width: 44px; height: 44px; flex: 0 0 44px; display: grid; place-items: center; border-radius: 12px; background: var(--brand-red); color: white; box-shadow: 0 6px 14px rgba(200, 16, 46, 0.22); }
        .reopen-picker-heading h3 { margin: 0 0 5px; color: var(--gray-900); font-size: 1.12rem; }
        .reopen-picker-heading p { margin: 0; color: var(--gray-600); font-size: 0.82rem; overflow-wrap: anywhere; }
        .reopen-picker-modal .modal-body { padding: 20px 24px 22px; }
        .reopen-picker-intro { margin: 0 0 16px; color: var(--gray-600); font-size: 0.82rem; line-height: 1.5; }
        .reopen-picker-list { display: grid; gap: 12px; }
        .reopen-picker-supplier { padding: 15px; border: 1px solid var(--gray-200); border-radius: 10px; background: var(--white); }
        .reopen-picker-supplier-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
        .reopen-picker-supplier-name { margin: 0; color: var(--gray-900); font-size: 0.9rem; font-weight: 750; }
        .reopen-picker-supplier-count { padding: 3px 8px; border-radius: 999px; color: #9a3412; background: #ffedd5; font-size: 0.7rem; font-weight: 700; white-space: nowrap; }
        .reopen-picker-stages { display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 9px; }
        .reopen-picker-stage { display: flex; align-items: center; justify-content: space-between; gap: 10px; min-height: 58px; padding: 10px 12px; border: 1px solid var(--gray-200); border-radius: 8px; background: var(--gray-50); color: var(--gray-800); text-align: left; font-size: 0.78rem; font-weight: 700; cursor: pointer; transition: 0.18s ease; }
        .reopen-picker-stage:hover { border-color: var(--brand-red); background: #fff5f5; color: var(--brand-red); transform: translateY(-1px); box-shadow: 0 4px 10px rgba(200, 16, 46, 0.1); }
        .reopen-picker-stage-label { display: grid; gap: 3px; }
        .reopen-picker-stage-subtitle { color: var(--gray-500); font-size: 0.7rem; font-weight: 500; }
        .reopen-picker-note { display: flex; gap: 8px; margin: 16px 0 0; padding: 10px 12px; border-radius: 8px; color: #7c2d12; background: #fff7ed; font-size: 0.75rem; line-height: 1.45; }
        .reopen-picker-empty { padding: 24px 12px; color: var(--gray-600); text-align: center; }
        .reopen-picker-modal .modal-footer { padding: 14px 24px; border: 0; background: var(--gray-50); }
        @media (max-width: 520px) { .reopen-picker-modal .modal-header, .reopen-picker-modal .modal-body, .reopen-picker-modal .modal-footer { padding-left: 18px; padding-right: 18px; } .reopen-picker-stages { display: grid; grid-template-columns: 1fr; } .reopen-picker-stage { justify-content: space-between; } }
      `}</style>

      <Sidebar />
      <div className="main-content">
        <Header title="Artworks" subtitle="All packaging artwork submissions and approvals" />
        <div className="page-body">
          <div className="page-container">

            {/* Toolbar */}
            <div className="card" style={{padding:'16px 20px', marginBottom:20}}>
              <div className="flex items-center gap-3" style={{flexWrap:'wrap'}}>
                <div style={{position:'relative', flex:'1', minWidth:200}}>
                  <Search size={16} style={{position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'var(--gray-400)'}} />
                  <input className="form-input" style={{paddingLeft:36}}
                    placeholder="Search by title, SKU, Design ID, supplier…"
                    value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <select className="form-input" style={{width:'auto', minWidth:140}}
                  value={stageFilter} onChange={e => setStageFilter(e.target.value)}>
                  <option value="">All Stages</option>
                  {[1,2,3,4].map(s => <option key={s} value={s}>Stage {s}: {STAGES[s].short}</option>)}
                </select>
                <select className="form-input" style={{width:'auto', minWidth:130}}
                  value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="on_hold">On Hold</option>
                </select>
                <select className="form-input" style={{width:'auto', minWidth:140}}
                  value={brandFilter} onChange={e => setBrandFilter(e.target.value)}>
                  <option value="">All Brands</option>
                  <option value="Hollandia">Hollandia</option>
                  <option value="Chivita">Chivita</option>
                </select>
                {isAdmin && (
                  <Link to="/artworks/new" className="btn btn-primary">
                    <Plus size={16} /> New Artwork
                  </Link>
                )}
              </div>
            </div>

            <div className="artwork-kpi-grid" aria-label="Artwork status metrics">
              {kpiCards.map(({ key, label, count, icon: Icon, color, background }) => (
                <button
                  key={key}
                  type="button"
                  className={`artwork-kpi ${activeTab === key ? 'active' : ''}`}
                  onClick={() => setActiveTab(key)}
                  aria-pressed={activeTab === key}
                >
                  <span className="artwork-kpi-icon" style={{color, background}}><Icon size={18} strokeWidth={2.2} /></span>
                  <span>
                    <span className="artwork-kpi-label">{label}</span>
                    <span className="artwork-kpi-value">{count}</span>
                  </span>
                </button>
              ))}
            </div>

            {/* Table */}
            <div className="card" style={{overflow:'hidden'}}>
              {loading ? (
                <div style={{padding:40, textAlign:'center'}}><div className="spinner spinner-dark" style={{width:28,height:28,margin:'0 auto'}} /></div>
              ) : filtered.length === 0 ? (
                <div className="empty-state">
                  <Image size={40} />
                  <h3>No artworks found</h3>
                  <p>{artworks.length === 0 ? 'No artworks have been uploaded yet.' : 'Try adjusting your filters.'}</p>
                  {isAdmin && artworks.length === 0 && (
                    <Link to="/artworks/new" className="btn btn-primary btn-sm" style={{marginTop:8}}>
                      <Plus size={14}/> Upload First Artwork
                    </Link>
                  )}
                </div>
              ) : (
                <>
                  <div className="table-wrap">
                    {showSupplierWorkflow ? (
                      <GroupedSupplierWorkflowTable
                        artworks={filtered.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)}
                        activeTab={activeTab}
                        onOpenArtwork={(artwork) => navigate(`/artworks/${artwork._id}`, { state: { activeTab, currentPage } })}
                        onRequestChanges={setRequestChangesArtwork}
                      />
                    ) : (
                    <table className="supplier-workflow-table">
                      <thead>
                        <tr>
                          <th>S/N</th>
                          <th>Artwork</th>
                          <th>Brand</th>
                          {activeTab === 'finalize' ? <th>Final copies uploaded</th> : <th>Current Stage</th>}
                          {activeTab === 'finalize' ? <th>Admin final copy status</th> : activeTab !== 'completed' && <th>Pending With</th>}
                          {activeTab === 'completed' && <th>Final copy uploaded</th>}
                          {activeTab === 'completed' && <th>Uploaded By</th>}
                          {filtered.some(a => a.currentStage === 3) && <th>Supplier</th>}
                          {isAdmin && ['completed', 'finalize'].includes(activeTab) && <th>Request changes</th>}
                          {activeTab === 'stage3' && (
                            <th style={{background:'#ecfdf5', color:'#166534', border:'1px solid rgba(22,101,54,0.2)', borderRight:'none', borderLeft:'none'}}>
                              Position Proof Status
                            </th>
                          )}
                          <th>Updated</th>
                          <th style={{width:120}}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage).map((a, index) => {
                          const serialNumber = (currentPage - 1) * rowsPerPage + index + 1;
                          return (
                            <tr
                              key={a._id}
                              onClick={() => navigate(`/artworks/${a._id}`, { state: { activeTab, currentPage } })}
                              className={`awl-row supplier-workflow-group-${serialNumber % 2 === 0 ? 'even' : 'odd'} supplier-workflow-group-start`}
                              style={{cursor:'pointer'}}
                            >
                              <td>{serialNumber}</td>
                              <td>
                                <div className="flex items-center gap-3">
                                  <div style={{
                                    width:40, height:40, borderRadius:'var(--radius-sm)',
                                    background:'var(--gray-100)', border:'1px solid var(--gray-200)',
                                    overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center',
                                    color:'var(--gray-400)', flexShrink:0
                                  }}>
                                    {(() => {
                                      const stageData = a[`stage${a.currentStage}`];
                                      const previewKind = getArtworkPreviewKind(stageData);

                                      if (previewKind === 'pdf') {
                                        return <FileText size={16} color="#ef4444" strokeWidth={2} />;
                                      }

                                      if (previewKind === 'image' && stageData?.artworkUrl) {
                                        return <img src={stageData.artworkUrl} alt={a.title} style={{width:'100%',height:'100%',objectFit:'cover'}} />;
                                      }

                                      return <Image size={16} />;
                                    })()}
                                  </div>
                                  <span style={{fontWeight:600, fontSize:'0.875rem'}}>{a.title}</span>
                                </div>
                              </td>
                              <td style={{fontSize:'0.8125rem', color:'var(--gray-700)', whiteSpace:'nowrap'}}>
                                {a.brand || '—'}
                              </td>
                              <td>
                                {activeTab === 'finalize' ? (
                                  (() => {
                                    const submissions = a.stage4?.submissions || [];
                                    const uploadedCount = submissions.filter(submission => submission.adminApproval?.uploadedAt).length;
                                    return `${uploadedCount} of ${submissions.length} uploaded`;
                                  })()
                                ) : activeTab === 'completed' ? (
                                  <span className="badge badge-success">Completed</span>
                                ) : (
                                  <span className={`badge badge-stage${a.currentStage}`}>
                                    Stage {a.currentStage}: {STAGES[a.currentStage]?.short}
                                  </span>
                                )}
                              </td>
                              {activeTab !== 'completed' && (
                                <td style={{fontSize:'0.8125rem', color:'var(--gray-600)', whiteSpace:'normal', wordBreak:'break-word', lineHeight:1.4}}>
                                  {activeTab === 'finalize'
                                    ? (a.stage4?.submissions || []).map((submission, submissionIndex) => {
                                      const supplier = submission.supplierId;
                                      const supplierName = supplier?.name || supplier?.email || `Supplier ${submissionIndex + 1}`;
                                      const uploaded = Boolean(submission.adminApproval?.uploadedAt);
                                      return (
                                        <div key={`${supplier?._id || supplier || submissionIndex}-${submissionIndex}`} style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:12, padding:'4px 0', borderBottom: submissionIndex < a.stage4.submissions.length - 1 ? '1px solid var(--gray-100)' : 'none'}}>
                                          <span>{supplierName}</span>
                                          <span style={{padding:'2px 7px', borderRadius:999, fontSize:'0.72rem', fontWeight:700, whiteSpace:'nowrap', color:uploaded ? '#166534' : '#9a3412', background:uploaded ? '#dcfce7' : '#ffedd5'}}>
                                            {uploaded ? 'Uploaded' : 'Pending'}
                                          </span>
                                        </div>
                                      );
                                    })
                                    : (() => {
                                      const pendingReviewers = isAdmin
                                        ? getPendingReviewersForArtwork(a)
                                        : getPendingReviewersForUser(a, user);
                                      return pendingReviewers.length > 0 ? pendingReviewers.join(', ') : '—';
                                    })()}
                                </td>
                              )}
                              {activeTab === 'completed' && (
                                <td>
                                  {(() => {
                                    const finalCopy = (a.stage4?.submissions || [])
                                      .map(submission => submission.adminApproval)
                                      .find(adminApproval => adminApproval?.uploadedAt);

                                    if (!finalCopy) return '—';
                                    const isPdf = finalCopy.artworkMimeType?.toLowerCase().includes('pdf') ||
                                      finalCopy.artworkFileName?.toLowerCase().endsWith('.pdf') ||
                                      finalCopy.artworkUrl?.toLowerCase().includes('.pdf');
                                    const thumbnailStyle = {
                                      width: 54,
                                      height: 54,
                                      borderRadius: 'var(--radius-sm)',
                                      border: '1px solid var(--gray-200)',
                                      background: 'var(--gray-50)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      overflow: 'hidden'
                                    };
                                    const thumbnail = isPdf ? (
                                      <div style={thumbnailStyle} title={finalCopy.artworkFileName || 'Open final copy'}>
                                        <FileText size={24} color="#ef4444" />
                                      </div>
                                    ) : finalCopy.artworkUrl ? (
                                      <img
                                        src={getCloudinaryThumbnailUrl(finalCopy.artworkUrl)}
                                        alt={finalCopy.artworkFileName || 'Final copy uploaded'}
                                        title={finalCopy.artworkFileName || 'Open final copy'}
                                        loading="lazy"
                                        decoding="async"
                                        style={{...thumbnailStyle, objectFit:'cover'}}
                                      />
                                    ) : (
                                      <div style={thumbnailStyle} title={finalCopy.artworkFileName || 'Uploaded final copy'}>
                                        <FileText size={24} color="var(--gray-500)" />
                                      </div>
                                    );

                                    return (
                                      <a
                                        href={finalCopy.artworkUrl || undefined}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={`Open ${finalCopy.artworkFileName || 'final copy'}`}
                                        onClick={e => e.stopPropagation()}
                                      >
                                        {thumbnail}
                                      </a>
                                    );
                                  })()}
                                </td>
                              )}
                              {activeTab === 'completed' && (
                                <td style={{fontSize:'0.8125rem', color:'var(--gray-600)', whiteSpace:'nowrap'}}>
                                  {((a.stage4?.submissions || [])
                                    .map(submission => submission.adminApproval)
                                    .find(adminApproval => adminApproval?.uploadedAt))?.uploaderName || '—'}
                                </td>
                              )}
                              {filtered.some(a => a.currentStage === 3) && (
                                <td style={{fontSize:'0.8125rem', color:'var(--gray-600)', whiteSpace:'nowrap'}}>
                                  {getArtworkSupplierDisplayName(a)}
                                </td>
                              )}
                              {isAdmin && ['completed', 'finalize'].includes(activeTab) && (
                                <td onClick={event => event.stopPropagation()}>
                                  {getReopenChoices(a).length > 0 ? (
                                    <button
                                      className="btn btn-secondary btn-sm"
                                      onClick={() => setRequestChangesArtwork(a)}
                                      title={`Request changes for ${a.title}`}
                                      style={{whiteSpace:'nowrap', color:'var(--brand-red)'}}
                                    >
                                      <RotateCcw size={14} /> Request changes
                                    </button>
                                  ) : '—'}
                                </td>
                              )}

                              {activeTab === 'stage3' && (
                                <td style={{fontSize:'0.8125rem', color:'#166534', background:'#ecfdf5', verticalAlign:'top', padding:'8px 8px', border:'1px solid rgba(22,101,54,0.2)', borderRight:'none', borderLeft:'none'}}>
                                  {(() => {
                                    const statuses = isAdmin
                                      ? getStage3SupplierSubmissionStatus(a)
                                      : getStageSupplierSubmissionStatusForUser(a, 3, user);
                                    if (statuses.length === 0) return '—';
                                    return statuses.map((status, index) => (
                                      <div key={status.supplierId} style={{display:'grid', gridTemplateColumns:'1fr auto', gap:8, alignItems:'center', padding:'2px 0', borderBottom: index < statuses.length - 1 ? '1px solid rgba(22,101,54,0.12)' : 'none'}}>
                                        <span style={{overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color:'var(--gray-800)', fontWeight:600, lineHeight:1.1}}>{status.supplierName}</span>
                                        <span style={{display:'inline-flex', alignItems:'center', gap:4, padding:'2px 6px', borderRadius:999, fontSize:'0.72rem', fontWeight:700, color: status.submitted ? '#166534' : '#7f1d1d', background: status.submitted ? 'rgba(22,101,54,0.12)' : 'rgba(220,38,38,0.12)'}}>
                                          {status.submitted ? <CheckCircle size={12} /> : <XCircle size={12} />}
                                          {status.submitted ? 'Submitted' : 'Pending'}
                                        </span>
                                      </div>
                                    ));
                                  })()}
                                </td>
                              )}
                              <td style={{fontSize:'0.8125rem', color:'var(--gray-500)', whiteSpace:'nowrap'}}>{formatDate(a.updatedAt)}</td>
                              <td style={{display:'flex', gap:6}}>
                                <Link
                                  to={`/artworks/${a._id}`}
                                  state={{ activeTab, currentPage }}
                                  className="btn btn-ghost btn-sm"
                                  onClick={e => e.stopPropagation()}
                                  title="View"
                                >
                                  <Eye size={14} />
                                </Link>
                                {isAdmin && (
                                  <button
                                    className="btn btn-ghost btn-sm"
                                    onClick={e => {
                                      e.stopPropagation();
                                      openDeleteModal(a._id, a.title);
                                    }}
                                    style={{color:'var(--danger)'}}
                                    title="Delete"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    )}
                  </div>
                  {Math.ceil(filtered.length / rowsPerPage) > 1 && (
                    <div className="pagination-bar" style={{padding:'16px 20px', display:'flex', justifyContent:'space-between', alignItems:'center', gap:12, flexWrap:'wrap'}}>
                      <div style={{fontSize:'0.875rem', color:'var(--gray-600)'}}>
                        Showing {Math.min(filtered.length, (currentPage - 1) * rowsPerPage + 1)} - {Math.min(filtered.length, currentPage * rowsPerPage)} of {filtered.length}
                      </div>
                      <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
                        <button
                          type="button"
                          className="pagination-button"
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                        {Array.from({ length: Math.ceil(filtered.length / rowsPerPage) }, (_, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className={`pagination-button ${currentPage === idx + 1 ? 'active' : ''}`}
                            onClick={() => setCurrentPage(idx + 1)}
                          >
                            {idx + 1}
                          </button>
                        ))}
                        <button
                          type="button"
                          className="pagination-button"
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filtered.length / rowsPerPage)))}
                          disabled={currentPage === Math.ceil(filtered.length / rowsPerPage)}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        title={`Delete "${deleteModal.title}"?`}
        message="This will remove all artwork records and images from Cloudinary. This action cannot be undone."
        onConfirm={confirmDelete}
        onCancel={closeDeleteModal}
        isDangerous={true}
        isLoading={deleting}
      />
      {requestChangesArtwork && (
        <RequestChangesPicker
          artwork={requestChangesArtwork}
          choices={getReopenChoices(requestChangesArtwork)}
          onClose={() => setRequestChangesArtwork(null)}
          onChoose={({ stageNum, supplierId, supplierName }) => {
            setRequestChangesArtwork(null);
            navigate(`/artworks/${requestChangesArtwork._id}`, {
              state: {
                activeTab,
                currentPage,
                reopenSubmission: { stageNum, supplierId, supplierName }
              }
            });
          }}
        />
      )}
    </div>
  );
}

function RequestChangesPicker({ artwork, choices, onClose, onChoose }) {
  return (
    <div className="modal-overlay">
      <div className="modal reopen-picker-modal" role="dialog" aria-modal="true" aria-labelledby="reopen-picker-title">
        <div className="modal-header">
          <div className="reopen-picker-heading">
            <div className="reopen-picker-icon"><RotateCcw size={18} /></div>
            <div>
              <h3 id="reopen-picker-title">Request changes</h3>
              <p>{artwork.title}</p>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <div className="modal-body">
          <p className="reopen-picker-intro">Select the supplier and the proof stage that needs correction. The supplier will be asked to submit an updated proof.</p>
          <div className="reopen-picker-list">
            {choices.map(choice => (
              <section className="reopen-picker-supplier" key={choice.supplierId}>
                <div className="reopen-picker-supplier-header">
                  <p className="reopen-picker-supplier-name">{choice.supplierName}</p>
                  <span className="reopen-picker-supplier-count">{choice.stages.length} stage{choice.stages.length === 1 ? '' : 's'} available</span>
                </div>
                <div className="reopen-picker-stages">
                  {choice.stages.sort((left, right) => left - right).map(stageNum => (
                    <button
                      className="reopen-picker-stage"
                      key={stageNum}
                      onClick={() => onChoose({ stageNum, supplierId: choice.supplierId, supplierName: choice.supplierName })}
                    >
                      <span className="reopen-picker-stage-label">
                        <span>Stage {stageNum}: {stageNum === 3 ? 'Position Proof' : 'Colour Proof'}</span>
                        <span className="reopen-picker-stage-subtitle">{stageNum === 3 ? 'Restarts this supplier’s Stage 4 review' : 'Requests a new colour proof'}</span>
                      </span>
                      <ArrowRight size={14} />
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
          {choices.length === 0 ? (
            <p className="reopen-picker-empty">No completed or review-ready supplier submissions can be reopened.</p>
          ) : (
            <p className="reopen-picker-note"><AlertTriangle size={16} style={{flexShrink:0}} />Stage 3 corrections remove that supplier’s Stage 4 approval so the full proof sequence can be reviewed again.</p>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function GroupedSupplierWorkflowTable({ artworks, activeTab, onOpenArtwork, onRequestChanges }) {
  const showPreview = activeTab === 'completed';
  const showChanges = ['completed', 'finalize'].includes(activeTab);
  const showStage3 = !['stage4', 'finalize', 'completed'].includes(activeTab);
  const showStage4 = !['stage3', 'completed'].includes(activeTab);
  const showFinalCopy = activeTab !== 'stage3';
  const showWorkflowState = activeTab !== 'completed';
  const groups = artworks.map((artwork, serialNumber) => {
    const suppliers = new Map();
    const add = (supplier) => {
      const id = supplierKey(supplier);
      if (id && !suppliers.has(id)) suppliers.set(id, supplier);
    };
    (artwork.supplierAssignmentInfo?.assignedSuppliers || artwork.suppliers || []).forEach(add);
    (artwork.stage3?.submissions || []).forEach(item => add(item.supplierId));
    (artwork.stage4?.submissions || []).forEach(item => add(item.supplierId));
    if (suppliers.size === 0) suppliers.set('__unassigned__', { name: 'No supplier assigned' });
    const supplierRows = [...suppliers.entries()].map(([id, supplier]) => {
      const stage3 = submissionForSupplier(artwork, 3, id);
      const stage4 = submissionForSupplier(artwork, 4, id);
      const stage3Label = supplierStageLabel(stage3, 3);
      const stage4Label = stage3Label !== 'Completed' && !stage4 ? 'Not started' : supplierStageLabel(stage4, 4);
      const finalLabel = stage4?.adminApproval?.uploadedAt ? 'Uploaded' : isStage4ReviewComplete(stage4 || {}) ? 'Needs final copy' : '-';
      const hasChanges = [stage3, stage4].some(item => (item?.approvals || []).some(approval => approval.status === 'changes_requested'));
      const workflowLabel = hasChanges ? 'Changes requested' : isArtworkFullyCompleted(artwork) ? 'Completed' : finalLabel === 'Needs final copy' ? 'Awaiting admin' : stage4Label === 'In review' ? 'Stage 4 review' : stage3Label === 'In review' ? 'Stage 3 review' : stage3Label === 'Awaiting upload' ? 'Awaiting supplier' : 'In progress';
      return { id, supplierName: supplier?.name || supplier?.email || stage3?.supplierId?.name || stage4?.supplierId?.name || 'Supplier', stage3Label, stage4Label, finalLabel, workflowLabel, finalCopy: stage4?.adminApproval };
    }).filter(row => !['stage3', 'stage4', 'finalize', 'changes_requested'].includes(activeTab) || supplierMatchesWorkflowTab(artwork, row.id, activeTab));
    return { artwork, serialNumber: serialNumber + 1, supplierRows };
  });

  return <table className="supplier-workflow-table">
    <thead><tr><th>S/N</th><th>Artwork</th><th>Brand</th><th>Assigned supplier</th>{showStage3 && <th>Stage 3: Position Proof</th>}{showStage4 && <th>Stage 4: Colour Proof</th>}{showFinalCopy && <th>Final copy</th>}{showPreview && <th>Final copy preview</th>}{showWorkflowState && <th>Workflow state</th>}<th>Updated</th>{showChanges && <th>Request changes</th>}</tr></thead>
    <tbody>{groups.flatMap(({ artwork, serialNumber, supplierRows }) => supplierRows.map((row, index) => {
      const first = index === 0;
      const finalCopy = row.finalCopy;
      const isPdf = finalCopy?.artworkMimeType?.toLowerCase().includes('pdf') ||
        finalCopy?.artworkFileName?.toLowerCase().endsWith('.pdf') ||
        finalCopy?.artworkUrl?.toLowerCase().includes('.pdf');
      const groupClass = `supplier-workflow-group-${serialNumber % 2 === 0 ? 'even' : 'odd'} ${first ? 'supplier-workflow-group-start' : ''}`;
      return <tr key={`${artwork._id}-${row.id}`} className={`awl-row ${groupClass}`} style={{ cursor: 'pointer' }} onClick={() => onOpenArtwork(artwork)}>
        {first && <td rowSpan={supplierRows.length} style={{ verticalAlign: 'top', fontWeight: 700 }}>{serialNumber}</td>}
        {first && <td rowSpan={supplierRows.length} style={{ verticalAlign: 'top', fontWeight: 600, minWidth: 190 }}>{artwork.title}</td>}
        {first && <td rowSpan={supplierRows.length} style={{ verticalAlign: 'top' }}>{artwork.brand || '-'}</td>}
        <td style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{row.supplierName}</td>{showStage3 && <td><WorkflowBadge label={row.stage3Label} /></td>}{showStage4 && <td><WorkflowBadge label={row.stage4Label} /></td>}
        {showFinalCopy && <td><WorkflowBadge label={row.finalLabel} />{finalCopy?.uploaderName && <div style={{ marginTop: 4, fontSize: '0.72rem', color: 'var(--gray-500)' }}>{finalCopy.uploaderName}</div>}</td>}
        {showPreview && <td>{finalCopy?.uploadedAt ? (finalCopy.artworkUrl && !isPdf ? <img src={getCloudinaryThumbnailUrl(finalCopy.artworkUrl)} alt={finalCopy.artworkFileName || 'Final copy'} loading="lazy" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 'var(--radius-sm)', border: '1px solid var(--gray-200)' }} /> : <div style={{ width: 50, height: 50, display: 'grid', placeItems: 'center', borderRadius: 'var(--radius-sm)', border: '1px solid var(--gray-200)' }}><FileText size={22} color={isPdf ? '#ef4444' : 'var(--gray-500)'} /></div>) : '-'}</td>}
        {showWorkflowState && <td><WorkflowBadge label={row.workflowLabel} /></td>}
        {first && <td rowSpan={supplierRows.length} style={{ verticalAlign: 'top' }}>{formatDate(artwork.updatedAt)}</td>}
        {showChanges && first && <td rowSpan={supplierRows.length} style={{ verticalAlign: 'top' }} onClick={event => event.stopPropagation()}>{getReopenChoices(artwork).length ? <button className="btn btn-secondary btn-sm" onClick={() => onRequestChanges(artwork)} style={{ whiteSpace: 'nowrap', color: 'var(--brand-red)' }}><RotateCcw size={14} /> Request changes</button> : '-'}</td>}
      </tr>;
    }))}</tbody>
  </table>;
}

// Retained temporarily for the non-grouped layout fallback.
// eslint-disable-next-line no-unused-vars
function SupplierWorkflowTable({ artworks, activeTab, onOpenArtwork, onRequestChanges }) {
  const rows = artworks.flatMap((artwork) => {
    const suppliers = new Map();
    const addSupplier = (supplier) => {
      const id = supplierKey(supplier);
      if (!id) return;
      if (!suppliers.has(id)) suppliers.set(id, { id, supplier });
    };

    (artwork.supplierAssignmentInfo?.assignedSuppliers || artwork.suppliers || []).forEach(addSupplier);
    (artwork.stage3?.submissions || []).forEach(submission => addSupplier(submission.supplierId));
    (artwork.stage4?.submissions || []).forEach(submission => addSupplier(submission.supplierId));

    const supplierRows = [...suppliers.values()].map(({ id, supplier }) => {
      const stage3 = submissionForSupplier(artwork, 3, id);
      const stage4 = submissionForSupplier(artwork, 4, id);
      const stage3Label = supplierStageLabel(stage3, 3);
      const stage4Label = stage3Label !== 'Completed' && !stage4
        ? 'Not started'
        : supplierStageLabel(stage4, 4);
      const finalLabel = stage4?.adminApproval?.uploadedAt
        ? 'Uploaded'
        : isStage4ReviewComplete(stage4 || {})
          ? 'Needs final copy'
          : '—';
      const supplierName = supplier?.name || supplier?.email || stage3?.supplierId?.name || stage4?.supplierId?.name || 'Supplier';
      const changesRequested = [stage3, stage4].some(submission =>
        (submission?.approvals || []).some(approval => approval.status === 'changes_requested')
      );
      const internalChangesRequested = ['stage1', 'stage2'].some(stage => artwork[stage]?.status === 'changes_requested');
      const workflowLabel = changesRequested || internalChangesRequested
        ? 'Changes requested'
        : artwork.overallStatus === 'completed'
          ? 'Completed'
          : finalLabel === 'Needs final copy'
            ? 'Awaiting admin'
            : stage4Label === 'In review'
              ? 'Stage 4 review'
              : stage3Label === 'In review'
                ? 'Stage 3 review'
                : stage3Label === 'Awaiting upload'
                  ? 'Awaiting supplier'
                  : 'In progress';

      return { artwork, id, supplierName, stage3Label, stage4Label, finalLabel, workflowLabel, uploadedBy: stage4?.adminApproval?.uploaderName || '—' };
    });
    return supplierRows;
  });

  return (
    <table>
      <thead>
        <tr>
          <th>Artwork</th>
          <th>Brand</th>
          <th>Assigned supplier</th>
          <th>Stage 3: Position Proof</th>
          <th>Stage 4: Colour Proof</th>
          <th>Final copy</th>
          <th>Workflow state</th>
          <th>Updated</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={`${row.artwork._id}-${row.id}`} className="awl-row" style={{ cursor: 'pointer' }} onClick={() => onOpenArtwork(row.artwork)}>
            <td style={{ fontWeight: 600, fontSize: '0.875rem' }}>{row.artwork.title}</td>
            <td style={{ fontSize: '0.8125rem', color: 'var(--gray-700)' }}>{row.artwork.brand || '—'}</td>
            <td style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{row.supplierName}</td>
            <td><WorkflowBadge label={row.stage3Label} /></td>
            <td><WorkflowBadge label={row.stage4Label} /></td>
            <td>
              <WorkflowBadge label={row.finalLabel} />
              {row.finalLabel === 'Uploaded' && row.uploadedBy !== '—' && <div style={{ marginTop: 4, color: 'var(--gray-500)', fontSize: '0.72rem' }}>{row.uploadedBy}</div>}
            </td>
            <td><WorkflowBadge label={row.workflowLabel} /></td>
            <td style={{ fontSize: '0.8125rem', color: 'var(--gray-500)' }}>{formatDate(row.artwork.updatedAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
